// skipcq: JS-W1028
import { useCallback } from 'react';
import BeenhereOutlinedIcon from '@mui/icons-material/BeenhereOutlined';
import ReviewsOutlinedIcon from '@mui/icons-material/ReviewsOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

const PROFILE_STAT_REVIEWS = 'reviews';
const PROFILE_STAT_TIMES_LOGGED = 'times-logged';

const staticStatSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  minWidth: 0,
};

const interactiveStatSx = {
  ...staticStatSx,
  cursor: 'pointer',
  appearance: 'none',
  background: 'none',
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  textDecoration: 'none',
  width: '100%',
  '&:hover': {
    background: 'none',
    textDecoration: 'none',
  },
};

/**
 * @param {unknown} value
 * @returns {number}
 */
function resolveStatCount(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
}

function StatCopy({ icon, label, count }) {
  return (
    <>
      {icon}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h6" component="p" fontWeight={600}>
          {count}
        </Typography>
      </Box>
    </>
  );
}

StatCopy.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};

function BeerProfileSummaryStat({ icon, label, count, ariaLabel, onActivate }) {
  const isInteractive = typeof onActivate === 'function';

  if (isInteractive) {
    return (
      <Box
        component="button"
        type="button"
        aria-label={ariaLabel}
        onClick={onActivate}
        sx={interactiveStatSx}
      >
        <StatCopy icon={icon} label={label} count={count} />
      </Box>
    );
  }

  return (
    <Box aria-label={ariaLabel} sx={staticStatSx}>
      <StatCopy icon={icon} label={label} count={count} />
    </Box>
  );
}

BeerProfileSummaryStat.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  onActivate: PropTypes.func,
};

function BeerProfileSummaryStatsCard({ totalReviews, checkinsCount, onOpenReviews }) {
  const reviewCount = resolveStatCount(totalReviews);
  const checkinCount = resolveStatCount(checkinsCount);
  const canOpenReviews = typeof onOpenReviews === 'function';

  const handleReviewsActivate = useCallback(() => {
    onOpenReviews(PROFILE_STAT_REVIEWS);
  }, [onOpenReviews]);

  const handleTimesLoggedActivate = useCallback(() => {
    onOpenReviews(PROFILE_STAT_TIMES_LOGGED);
  }, [onOpenReviews]);

  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          <BeerProfileSummaryStat
            icon={<ReviewsOutlinedIcon color="action" aria-hidden />}
            label="Reviews"
            count={reviewCount}
            ariaLabel={`Reviews: ${reviewCount}`}
            onActivate={canOpenReviews ? handleReviewsActivate : undefined}
          />
          <BeerProfileSummaryStat
            icon={<BeenhereOutlinedIcon color="action" aria-hidden />}
            label="Times logged"
            count={checkinCount}
            ariaLabel={`Times logged: ${checkinCount}`}
            onActivate={canOpenReviews ? handleTimesLoggedActivate : undefined}
          />
        </Box>
      </CardContent>
    </Card>
  );
}

BeerProfileSummaryStatsCard.propTypes = {
  totalReviews: PropTypes.number,
  checkinsCount: PropTypes.number,
  onOpenReviews: PropTypes.func,
};

export default BeerProfileSummaryStatsCard;
